import { ActivityEditorFormProps } from '@/routes/activities/ActivityEditor.types'
import {
  ButtonItem,
  ButtonOptions,
  CustomRule,
  Form as DXForm,
  FormRef,
  GroupItem,
  RequiredRule,
  SimpleItem,
} from 'devextreme-react/form'
import { useCallback, useEffect, useRef, useState } from 'react'
import { FieldDataChangedEvent } from 'devextreme/ui/form'
import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import { ArraySourceComposer } from '@/auth/api/connector'
import DataSource from 'devextreme/data/data_source'
import {
  azienda,
  sede,
  contatto_aziendale,
  commessa,
  gruppo_aziendale,
  stato_att_comm,
} from '@/model/qsadminapi/QsAdminApiModuleModel'
import { useNavigate, useParams, useRevalidator, useSearchParams } from 'react-router-dom'
import { ValueChangedEvent as ValueChangedEventSelectBox } from 'devextreme/ui/select_box'
import { StatoCommessa } from '@/routes/projects/ProjectEditor.enums'
import { ClickEvent } from 'devextreme/ui/button'
import notify from 'devextreme/ui/notify'
import LoadingPanel from '@/components/loading-panel/LoadingPanel'
import {
  customButton,
  isUserRoleAllowed,
  onInitializedHtmlEditor,
  onValueChangedHtmlEditor,
  toolbarHtmlEditor,
} from '@/routes/utils'
import FormPopupProject from '@/components/form-popup-project/FormPopupProject'
import { PopupRef } from 'devextreme-react/cjs/popup'
import FormPopupClientNote from '@/components/form-popup-client-note/FormPopupClientNote'
import { useScreenSize } from '@/themes/media-query'
import { Roles } from '@/auth/azure/Roles'
import FormPopupContact from '@/routes/contacts/person/FormPopupContact'
import { LookupTypes } from 'devextreme-react/cjs/lookup'
import { StatoAttivitaComm, TipologiaAttivitaComm } from '@/routes/activities/Activity.enums'
import { confirm } from 'devextreme/ui/dialog'
import { ValidationCallbackData } from 'devextreme/common'

const ActivityEditorForm = (props: ActivityEditorFormProps) => {
  const {
    creating,
    activity,
    commesse,
    contatti,
    aziende,
    stati,
    tipologie,
    prodotti,
    piattaforme,
    isRoleAllowed,
    userInfo,
    fetcher,
  } = props
  const { clientId } = useParams()
  const [searchParams] = useSearchParams()
  const projectId = searchParams.get('projectId')
  const activityPrecId = searchParams.get('activityPrecId')

  const currentScreenSize = useScreenSize()
  const formRef = useRef<FormRef>(null)
  const navigate = useNavigate()
  const revalidator = useRevalidator()
  const { service } = useQsAdminApiManager()

  const [currentCompany, setCurrentCompany] = useState<azienda | null | undefined>(
    clientId ? aziende[0] : (aziende.find((a) => a.id === activity.sede?.azienda?.id) ?? activity.sede?.azienda),
  )
  const [currentCompanyChanged, setCurrentCompanyChanged] = useState<boolean>(
    clientId && creating && !projectId ? true : false,
  )

  const [isDaApprovare, setIsDaApprovare] = useState<boolean>(
    activity.tipologia?.id === TipologiaAttivitaComm.DEMO && activity.stato?.id === StatoAttivitaComm['DA APPROVARE'],
  )

  const [isNonApprovata, setIsNonApprovata] = useState<boolean>(
    activity.tipologia?.id === TipologiaAttivitaComm.DEMO && activity.stato?.id === StatoAttivitaComm['NON APPROVATA'],
  )

  const [sediDataSource, setSediDataSource] = useState<DataSource>(
    ArraySourceComposer('id', activity.sede?.azienda?.sedi),
  )
  const [currentSiteChanged, setCurrentSiteChanged] = useState<boolean>(false)

  const [contattiDataSource, setContattiDataSource] = useState<DataSource>(ArraySourceComposer('id', contatti))
  const [commesseDataSource, setCommesseDataSource] = useState<DataSource>(ArraySourceComposer('id', commesse))
  const [statiDataSource, setStatiDataSource] = useState<DataSource>(ArraySourceComposer('id', stati))

  const popupNoteClienteRef = useRef<PopupRef>(null)
  const popupNuovoContattoRef = useRef<PopupRef>(null)
  const popupNuovaCommessaRef = useRef<PopupRef>(null)

  const [currentSite, setCurrentSite] = useState<sede>()

  const [currentContact, setCurrentContact] = useState<contatto_aziendale>()
  const [currentGruppoAziendale, setCurrentGruppoAziendale] = useState<gruppo_aziendale | null | undefined>(
    activity?.sede?.azienda?.gruppo_aziendale,
  )

  const getAziendaData = useCallback(async () => {
    const aziendaData = await service.azienda(Number(currentCompany?.id)).query((builder, azienda) => {
      builder.expanding('gruppo_aziendale', (gruppoBuilder, gruppo) => {
        gruppoBuilder.select('id', 'nome')
      })
      builder.select('gruppo_aziendale')
    })
    return aziendaData.data
  }, [currentCompany?.id, service])

  const getCompanySites = useCallback(async () => {
    const sitesData = await service.sede().query((builder, sede) => {
      builder.filter(sede.azienda.props.id.eq(Number(currentCompany?.id)))
      builder.orderBy(sede.principale.desc())
      builder.expanding('azienda', (aziendaSedeBuilder, azienda) => {
        aziendaSedeBuilder.expanding('sedi', (sediAziendaSedeBuilder, sedeAzienda) => {
          sediAziendaSedeBuilder.select('id', 'nome', 'note')
          sediAziendaSedeBuilder.orderBy(sedeAzienda.principale.desc())
        })
        aziendaSedeBuilder.expanding('agente', (agenteBuilder, agente) => {
          agenteBuilder.expanding('commerciale_qs', (commercialeQsBuilder, commercialeQs) => {
            commercialeQsBuilder.select('id', 'fullname')
          })
          agenteBuilder.select('id', 'commerciale_qs')
        })
        aziendaSedeBuilder.expanding('rivenditore', (rivenditoreBuilder, rivenditore) => {
          rivenditoreBuilder.select('id', 'nome')
        })
        aziendaSedeBuilder.expanding('gruppo_aziendale', (gruppo_aziendaleBuilder, gruppo_aziendale) => {
          gruppo_aziendaleBuilder.select('id', 'nome')
        })
        aziendaSedeBuilder.select('id', 'nome', 'sedi', 'agente', 'rivenditore', 'gruppo_aziendale')
      })
      builder.select('id', 'nome', 'azienda', 'note')
    })
    return sitesData.data.value
  }, [currentCompany?.id, service])

  const getSiteContacts = useCallback(async () => {
    const contactsData = await service.contatto_aziendale().query((builder, contatto) => {
      builder.filter(contatto.sede.props.id.eq(Number(activity.sede?.id)))
      builder.filter(contatto.attivo.eq(true))
      builder.orderBy(contatto.fullname.asc())
      builder.select('id', 'fullname')
    })
    return contactsData.data.value
  }, [activity.sede?.id, service])

  const getSiteProjects = useCallback(async () => {
    const projectsData = await service.commessa().query((builder, commessa) => {
      builder.filter(
        currentGruppoAziendale
          ? commessa.sede.props.azienda.props.gruppo_aziendale.props.id.eq(Number(currentGruppoAziendale.id))
          : commessa.sede.props.azienda.props.id.eq(Number(currentCompany?.id)),
      )
      builder.filter(commessa.stato.props.id.eq(StatoCommessa.APERTA))
      builder.expanding('sede', (sedeBuilder) => {
        sedeBuilder.expanding('azienda', (aziendaBuilder, azienda) => {
          aziendaBuilder.select('id', 'nome')
        })
        sedeBuilder.select('id', 'nome', 'azienda')
      })
      builder.select('id', 'ded_Dis', 'titolo', 'sede')
      builder.orderBy(commessa.ded_Dis.desc())
    })
    return projectsData.data.value
  }, [service, currentGruppoAziendale, currentCompany?.id])

  //trigger sull'onchanged di azienda
  useEffect(() => {
    if (!currentCompanyChanged) return
    console.log('COMPANY EFFECT', currentCompany)
    if (currentCompany) {
      getCompanySites().then((sites: sede[]) => {
        console.log('SITES', sites)
        setSediDataSource(ArraySourceComposer('id', sites))
        formRef.current?.instance().updateData('sede', sites[0])
        setCurrentSiteChanged(true)
      })
      getAziendaData().then((company: azienda) => {
        console.log('data', company)
        setCurrentGruppoAziendale(company.gruppo_aziendale ?? null)
      })
      setCurrentCompanyChanged(false)
    }
  }, [currentCompanyChanged, currentCompany, getCompanySites, getAziendaData])

  //trigger sull'onchanged di sede
  useEffect(() => {
    if (!currentSiteChanged) return
    console.log('SITE EFFECT', activity.sede)
    if (activity.sede) {
      getSiteContacts().then((contacts: contatto_aziendale[]) => {
        console.log('contacts', contacts)
        setContattiDataSource(ArraySourceComposer('id', contacts))
        formRef.current?.instance().updateData('contatto_aziendale', null)
      })
      if (!projectId && !clientId && !activityPrecId)
        getSiteProjects().then((commesse: commessa[]) => {
          console.log('commesse', commesse)
          setCommesseDataSource(ArraySourceComposer('id', commesse))
          formRef.current?.instance().updateData('commessa', null)
        })
      setCurrentSiteChanged(false)
    }
  }, [currentSiteChanged, activity.sede, getSiteContacts, getSiteProjects, projectId, clientId, activityPrecId])

  useEffect(() => {
    setCommesseDataSource(ArraySourceComposer('id', commesse))
    setStatiDataSource(ArraySourceComposer('id', stati))
    setIsDaApprovare(
      activity.tipologia?.id === TipologiaAttivitaComm.DEMO && activity.stato?.id === StatoAttivitaComm['DA APPROVARE'],
    )
    setIsNonApprovata(
      activity.tipologia?.id === TipologiaAttivitaComm.DEMO &&
        activity.stato?.id === StatoAttivitaComm['NON APPROVATA'],
    )
  }, [activity.stato?.id, activity.tipologia?.id, commesse, stati])

  return (
    <>
      <LoadingPanel visible={!(fetcher?.state === 'idle' && revalidator.state === 'idle')} />
      <DXForm
        id={'activity_form'}
        formData={activity}
        labelLocation={'top'}
        showValidationSummary={true}
        onFieldDataChanged={(e: FieldDataChangedEvent) => {
          if (
            formRef.current?.instance().option().isDirty !== null &&
            formRef.current?.instance().option().isDirty !== undefined
          ) {
            console.log('is dirty', formRef.current?.instance().option().isDirty)
            formRef.current
              .instance()
              .getButton('btn-save')
              ?.option('disabled', !formRef.current?.instance().option().isDirty)
            formRef.current
              .instance()
              .getButton('btn-cancel')
              ?.option('disabled', !formRef.current?.instance().option().isDirty)
          }
        }}
        validationGroup="activityValidation"
        ref={formRef}
        readOnly={!isRoleAllowed || (isDaApprovare && !creating) || isNonApprovata}
      >
        <GroupItem colCountByScreen={{ xs: 1, sm: 2, md: 2, lg: 2 }}>
          <GroupItem colCountByScreen={{ xs: 1, sm: 1, md: 1, lg: 2 }}>
            <SimpleItem
              label={{ text: 'Azienda' }}
              editorType="dxSelectBox"
              editorOptions={{
                buttons: [
                  customButton('open', (e: ClickEvent) => {
                    if (currentCompany) window.open(`/clients/${currentCompany.id}`, '_blank')
                    else notify(`Deve essere selezionata un' azienda per poterla aprire.`, 'warning', 3000)
                  }),
                  { name: 'dropDown', location: 'after' },
                ],
                value: currentCompany,
                dataSource: ArraySourceComposer('id', aziende),
                placeholder: "Seleziona l'azienda...",
                displayExpr: 'nome',
                searchEnabled: true,
                dropDownOptions: {
                  showTitle: true,
                  title: "Selezionare l'azienda",
                  hideOnOutsideClick: true,
                },
                readOnly: !isRoleAllowed || clientId,
                onValueChanged: (e: ValueChangedEventSelectBox) => {
                  if (e.previousValue?.id === e.value?.id) return
                  console.log('COMPANY EVENT CHANGED', e.previousValue, e.value)
                  setCurrentCompany(e.value)
                  setCurrentCompanyChanged(true)
                },
              }}
            >
              <CustomRule
                validationCallback={(options) => {
                  if (options.value) {
                    const value: azienda = options.value
                    // Cerca nella lista completa come fallback
                    const fullAzienda = aziende.find((a) => a.id === value.id) ?? value
                    return !!fullAzienda.p_iva
                  }
                  return true
                }}
                message={`L'azienda selezionata è senza partita IVA`}
              ></CustomRule>
            </SimpleItem>
            <SimpleItem
              dataField="sede"
              label={{ text: 'Sede' }}
              editorType="dxSelectBox"
              editorOptions={{
                buttons: [
                  customButton(
                    'info',
                    () => {
                      if (activity.sede) {
                        popupNoteClienteRef.current?.instance().show()
                      } else notify('Deve essere selezionata una sede per aprire le note.', 'warning', 3000)
                    },
                    false,
                    'Note cliente',
                  ),
                  {
                    name: 'clear',
                    location: 'after',
                  },
                  { name: 'dropDown', location: 'after' },
                ],
                dataSource: sediDataSource,
                placeholder: 'Seleziona la sede...',
                displayExpr: 'nome',
                searchEnabled: true,
                showClearButton: true,
                showDropDownButton: true,
                dropDownButtonTemplate: 'dropDownButton',
                dropDownOptions: {
                  showTitle: true,
                  title: 'Selezionare la sede',
                  hideOnOutsideClick: true,
                },
                readOnly: !isRoleAllowed || (isDaApprovare && !creating) || isNonApprovata,
                onValueChanged: (e: ValueChangedEventSelectBox) => {
                  if (e.previousValue?.id === e.value?.id) return
                  console.log('SITE EVENT CHANGED', e.previousValue, e.value)
                  setCurrentSiteChanged(true)
                },
              }}
            >
              <CustomRule
                message="Sede obbligatoria se tipologia è 'DEMO'"
                validationCallback={(options: ValidationCallbackData) => {
                  console.log('options', options)
                  if (activity.tipologia?.id !== TipologiaAttivitaComm.DEMO) return true
                  return !!options.value
                }}
              ></CustomRule>
              <CustomRule
                message="Sede obbligatoria se 'Azienda' presente"
                validationCallback={(options: ValidationCallbackData) => {
                  if (!currentCompany) return true
                  return activity.sede ? true : false
                }}
              ></CustomRule>
              <CustomRule
                reevaluate={true}
                message="Sede obbligatoria se 'Contatto' non presente"
                validationCallback={(options: ValidationCallbackData) => {
                  return !(!activity.contatto_aziendale && !options.value)
                }}
              ></CustomRule>
            </SimpleItem>
            <SimpleItem
              dataField="inizio"
              editorOptions={{
                displayFormat: 'dd/MM/yyyy',
                type: 'date',
                pickerType: 'calendar',
                applyValueMode: 'instantly',
              }}
              editorType="dxDateBox"
              label={{ text: 'Data Inizio' }}
            >
              <RequiredRule message="Data inizio obbligatoria"></RequiredRule>
            </SimpleItem>
            <SimpleItem
              dataField="fine"
              editorOptions={{
                displayFormat: 'dd/MM/yyyy',
                type: 'date',
                pickerType: 'calendar',
                applyValueMode: 'instantly',
                showClearButton: true,
              }}
              editorType="dxDateBox"
              label={{ text: 'Data Fine' }}
            ></SimpleItem>
            <SimpleItem
              dataField="tipologia"
              label={{ text: 'Tipo' }}
              editorType="dxLookup"
              editorOptions={{
                dataSource: ArraySourceComposer('id', tipologie),
                placeholder: 'Seleziona la tipologia...',
                displayExpr: 'nome',
                searchEnabled: true,
                dropDownCentered: true,
                dropDownOptions: {
                  showTitle: true,
                  title: 'Selezionare tipo',
                  hideOnOutsideClick: true,
                },
                readOnly: !creating,
                onValueChanged: (e: LookupTypes.ValueChangedEvent) => {
                  console.log('e', e)
                  if (e.value === e.previousValue) return
                  if (!creating) return
                  if (e.value.id === TipologiaAttivitaComm.DEMO) {
                    formRef.current?.instance().updateData(
                      'stato',
                      stati.find((stato: stato_att_comm) => stato.id === StatoAttivitaComm['DA APPROVARE']),
                    )
                    setIsDaApprovare(true)
                  } else {
                    formRef.current?.instance().updateData('stato', null)
                    formRef.current?.instance().updateData('commessa', null)
                    setIsDaApprovare(false)
                  }
                },
              }}
            >
              <RequiredRule message="Tipo obbligatorio"></RequiredRule>
            </SimpleItem>
            <SimpleItem
              dataField="stato"
              label={{ text: 'Stato' }}
              editorType="dxLookup"
              editorOptions={{
                dataSource: statiDataSource,
                placeholder: 'Seleziona lo stato...',
                displayExpr: 'nome',
                searchEnabled: true,
                dropDownCentered: true,
                dropDownOptions: {
                  showTitle: true,
                  title: 'Selezionare lo stato',
                  hideOnOutsideClick: true,
                },
                readOnly: isDaApprovare || isNonApprovata || !isRoleAllowed,
              }}
            >
              <RequiredRule message="Stato obbligatorio"></RequiredRule>
            </SimpleItem>
            <SimpleItem
              dataField="contatto_aziendale"
              label={{ text: 'Contatto' }}
              editorType="dxSelectBox"
              editorOptions={{
                buttons: [
                  customButton(
                    'add',
                    (e: ClickEvent) => {
                      if (activity.sede) {
                        const contact: contatto_aziendale = {
                          id: 0,
                          fullname: null,
                          ad_recipient: false,
                          nome: null,
                          cognome: null,
                          ruolo: null,
                          note: null,
                          titolo: null,
                          telefono: null,
                          attivo: true,
                          sede: activity.sede,
                          hasLinkedObjects: false,
                          ricezione_rapportino: false,
                          responsabile_tecnico: false,
                          updated_at: null,
                          created_at: null,
                          rinnovo_offerta_qs: false,
                        }
                        setCurrentContact(contact)
                        popupNuovoContattoRef.current?.instance().show()
                      } else notify(`Deve essere selezionata una sede per poter inserire un contatto.`, 'warning', 3000)
                    },
                    !isRoleAllowed || (isDaApprovare && !creating),
                  ),
                  { name: 'clear', location: 'after' },
                  { name: 'dropDown', location: 'after' },
                ],
                dataSource: contattiDataSource,
                placeholder: 'Seleziona il contatto...',
                displayExpr: 'fullname',
                searchEnabled: true,
                showDropDownButton: true,
                showClearButton: true,
                dropDownButtonTemplate: 'dropDownButton',
                dropDownOptions: {
                  showTitle: true,
                  title: 'Selezionare il contatto',
                  hideOnOutsideClick: true,
                },
              }}
            >
              <CustomRule
                reevaluate={true}
                message="Contatto obbligatorio se 'Sede' non presente"
                validationCallback={(options: ValidationCallbackData) => {
                  return !(!activity.sede && !options.value)
                }}
              />
            </SimpleItem>
            <SimpleItem
              dataField="commessa"
              label={{ text: 'Commessa' }}
              editorType="dxSelectBox"
              editorOptions={{
                buttons: [
                  customButton(
                    'add',
                    () => {
                      if (activity.sede) {
                        setCurrentSite(activity.sede)
                        popupNuovaCommessaRef.current?.instance().show()
                      } else
                        notify(`Deve essere selezionata un' azienda per poter creare una commessa.`, 'warning', 3000)
                    },
                    !isRoleAllowed ||
                      (projectId !== null && projectId !== undefined) ||
                      isDaApprovare ||
                      isNonApprovata,
                  ),
                  customButton(
                    'open',
                    () => {
                      if (activity.commessa) window.open(`/projects/${activity?.commessa?.id}`, '_blank')
                      else notify(`Deve essere selezionata una commessa per poterla aprire.`, 'warning', 3000)
                    },
                    false,
                  ),
                  {
                    name: 'clear',
                    location: 'after',
                  },
                  { name: 'dropDown', location: 'after' },
                ],
                dataSource: commesseDataSource,
                placeholder: 'Seleziona commessa...',
                displayExpr: (item: commessa) => {
                  return item
                    ? `${item.ded_Dis}${item.titolo ? ' - ' : ''}${item.titolo ?? ''}${item.sede?.azienda?.nome ? ' - ' : ''}${item.sede?.azienda?.nome ?? ''}${item.sede?.nome ? ' - ' : ''}${item.sede?.nome ?? ''}`
                    : ``
                },
                showDropDownButton: true,
                dropDownButtonTemplate: 'dropDownButton',
                searchEnabled: true,
                showClearButton: true,
                dropDownOptions: {
                  showTitle: true,
                  title: 'Selezionare commessa',
                  hideOnOutsideClick: true,
                  width: currentScreenSize.isLarge ? '150%' : '',
                  resizeEnabled: currentScreenSize.isLarge ? true : false,
                },
                readOnly:
                  !isRoleAllowed || (projectId !== null && projectId !== undefined) || isDaApprovare || isNonApprovata,
              }}
            ></SimpleItem>
            <SimpleItem
              dataField="prodotto"
              label={{ text: 'Prodotto' }}
              editorType="dxLookup"
              editorOptions={{
                dataSource: ArraySourceComposer('id', prodotti),
                placeholder: 'Seleziona il prodotto...',
                displayExpr: 'nome',
                searchEnabled: true,
                dropDownCentered: true,
                showClearButton: true,
                dropDownOptions: {
                  showTitle: true,
                  title: 'Selezionare il prodotto',
                  hideOnOutsideClick: true,
                },
              }}
            ></SimpleItem>
            <SimpleItem
              dataField="piattaforma"
              label={{ text: 'Piattaforma' }}
              editorType="dxLookup"
              editorOptions={{
                dataSource: ArraySourceComposer('id', piattaforme),
                placeholder: 'Seleziona la piattaforma...',
                displayExpr: 'nome',
                searchEnabled: true,
                dropDownCentered: true,
                showClearButton: true,
                dropDownOptions: {
                  showTitle: true,
                  title: 'Selezionare la piattaforma',
                  hideOnOutsideClick: true,
                },
              }}
            ></SimpleItem>
            <GroupItem colCount={2} colSpan={2}></GroupItem>
            <ButtonItem name="goToPrecActivity" verticalAlignment={'bottom'} colSpan={1}>
              <ButtonOptions
                text={'Precedente'}
                icon={'arrowleft'}
                type="default"
                stylingMode="text"
                disabled={!activity?.precedente}
                onClick={() => {
                  navigate({
                    pathname: `/activities/${activity?.precedente?.id}`,
                  })
                }}
              />
            </ButtonItem>
            <ButtonItem name="addFollowUp" verticalAlignment={'bottom'} colSpan={1}>
              <ButtonOptions
                text={'Follow Up'}
                icon={'add'}
                type="default"
                stylingMode="text"
                disabled={creating || !isRoleAllowed || isDaApprovare || isNonApprovata}
                onClick={() => {
                  navigate({
                    pathname: '/activities/new',
                    search: `?activityPrecId=${activity?.id}`,
                  })
                }}
              />
            </ButtonItem>
          </GroupItem>
          <GroupItem colCount={1}>
            <SimpleItem
              dataField="descrizione"
              label={{ visible: true }}
              editorType="dxHtmlEditor"
              editorOptions={{
                stylingMode: 'filled',
                valueType: 'html',
                toolbar: toolbarHtmlEditor(currentScreenSize),
                mediaResizing: {
                  enabled: true,
                },
                imageUpload: {
                  tabs: ['file', 'url'],
                  fileUploadMode: 'base64',
                },
                height: 330,
                onValueChanged: onValueChangedHtmlEditor,
                onInitialized: onInitializedHtmlEditor,
              }}
            >
              <RequiredRule message="Descrizione obbligatoria"></RequiredRule>
            </SimpleItem>
          </GroupItem>
        </GroupItem>
        <GroupItem visible={isRoleAllowed} cssClass="last-group">
          <GroupItem
            cssClass="buttons-group"
            colCount={
              isDaApprovare &&
              (userInfo?.roles?.hasRole(Roles.GlobalAdministrator) ||
                (userInfo?.roles?.hasRole(Roles.Supervisor) && userInfo.roles.hasRole(Roles.Sales))) &&
              !creating
                ? 2
                : creating
                  ? 3
                  : 4
            }
          >
            {!(
              isDaApprovare &&
              (userInfo?.roles?.hasRole(Roles.GlobalAdministrator) ||
                (userInfo?.roles?.hasRole(Roles.Supervisor) && userInfo.roles.hasRole(Roles.Sales))) &&
              !creating
            ) ? (
              <>
                <ButtonItem name="btn-task">
                  <ButtonOptions
                    text="TASK"
                    icon="checklist"
                    useSubmitBehavior={false}
                    width={'150px'}
                    onClick={(e: ClickEvent) => {
                      window.open(`/tasks/new?activityId=${activity.id}`, '_blank')
                    }}
                    disabled={
                      creating ||
                      activity.tipologia?.id !== TipologiaAttivitaComm.DEMO ||
                      (activity.tipologia?.id === TipologiaAttivitaComm.DEMO &&
                        (activity.stato?.id === StatoAttivitaComm.COMPLETATA ||
                          activity.stato?.id === StatoAttivitaComm['COMPLETATA CON VENDITA'] ||
                          activity.stato?.id === StatoAttivitaComm['DA APPROVARE'] ||
                          activity.stato?.id === StatoAttivitaComm['NON APPROVATA'])) ||
                      !isUserRoleAllowed(userInfo?.roles, [
                        Roles.GlobalAdministrator,
                        Roles.Administrator,
                        Roles.Supervisor,
                      ])
                    }
                  />
                </ButtonItem>
                <ButtonItem name="btn-commessa">
                  <ButtonOptions
                    text="Commessa"
                    icon="check"
                    useSubmitBehavior={false}
                    width={'150px'}
                    disabled={creating || !isRoleAllowed || isDaApprovare || isNonApprovata}
                    onClick={(e: ClickEvent) => {
                      if (activity?.commessa) {
                        notify(
                          `Attività commerciale già collegata a commessa. Commessa: ${activity.commessa.id}`,
                          'warning',
                          3000,
                        )
                      } else {
                        navigate({
                          pathname: '/projects/new',
                          search: `?activityId=${activity?.id}`,
                        })
                      }
                    }}
                  />
                </ButtonItem>
                <ButtonItem name="btn-save">
                  <ButtonOptions
                    disabled={!formRef.current?.instance().option().isDirty}
                    icon="save"
                    text="Salva"
                    width="150px"
                    onClick={async () => {
                      const validationResult = formRef.current?.instance().validate()
                      if (!validationResult?.isValid) return
                      console.log('validation ok')
                      console.log('formData', activity)
                      if (activity) {
                        fetcher?.submit(JSON.stringify(activity), {
                          method: creating ? 'POST' : 'PUT',
                          encType: 'application/json',
                        })
                      } else console.error('no data tu submit')
                    }}
                  />
                </ButtonItem>
                {!creating && (
                  <ButtonItem name="btn-cancel">
                    <ButtonOptions
                      disabled={!formRef.current?.instance().option().isDirty}
                      width="150px"
                      text="Annulla"
                      icon="undo"
                      onClick={() => {
                        revalidator.revalidate()
                      }}
                    />
                  </ButtonItem>
                )}
              </>
            ) : (
              <>
                <ButtonItem name="btn-approve">
                  <ButtonOptions
                    text="Approva"
                    visible={isUserRoleAllowed(userInfo?.roles, [Roles.Supervisor, Roles.GlobalAdministrator])}
                    icon="check"
                    useSubmitBehavior={false}
                    width={'150px'}
                    onClick={(e: ClickEvent) => {
                      const result = confirm(
                        `<i>Vuoi davvero <b>approvare</b> l'attività commerciale <b>${activity.ded_Dis}</b>?</i>`,
                        'Approvazione attività commerciale',
                      )
                      result.then(async (dialogResult) => {
                        if (dialogResult === false) return
                        fetcher?.submit(
                          JSON.stringify({
                            activity,
                            state: stati.find((stato) => stato.id === StatoAttivitaComm.APPROVATA),
                          }),
                          {
                            method: 'put',
                            action: `/activities/${activity.id}/approve`,
                            encType: 'application/json',
                          },
                        )
                      })
                    }}
                  />
                </ButtonItem>
                <ButtonItem name="btn-notApprove">
                  <ButtonOptions
                    icon="close"
                    text="Non Approva"
                    visible={isUserRoleAllowed(userInfo?.roles, [Roles.Supervisor, Roles.GlobalAdministrator])}
                    width="150px"
                    onClick={async () => {
                      const result = confirm(
                        `<i>Vuoi davvero <b>non approvare</b> l'attività commerciale <b>${activity.ded_Dis}</b>?</i>`,
                        'Approvazione attività commerciale',
                      )
                      result.then(async (dialogResult) => {
                        if (dialogResult === false) return
                        fetcher?.submit(
                          JSON.stringify({
                            activity,
                            state: stati.find((stato) => stato.id === StatoAttivitaComm['NON APPROVATA']),
                          }),
                          {
                            method: 'put',
                            action: `/activities/${activity.id}/approve`,
                            encType: 'application/json',
                          },
                        )
                      })
                    }}
                  />
                </ButtonItem>
              </>
            )}
          </GroupItem>
        </GroupItem>
      </DXForm>
      <FormPopupContact
        migrating={false}
        popupRef={popupNuovoContattoRef}
        contact={currentContact}
        onContactSaved={(newContact) => {
          popupNuovoContattoRef.current?.instance().hide()
          getSiteContacts().then((contacts: contatto_aziendale[]) => {
            formRef.current
              ?.instance()
              .updateData('contatto_aziendale', contacts.filter((persona) => persona.id === newContact?.id)[0])
            setContattiDataSource(ArraySourceComposer('id', contacts))
          })
        }}
      ></FormPopupContact>
      <FormPopupProject
        popupRef={popupNuovaCommessaRef}
        sede={currentSite}
        azienda={currentCompany}
        refreshCommessa={async () => {
          getSiteProjects().then((res: commessa[]) => {
            setCommesseDataSource(ArraySourceComposer('id', res))
            formRef.current?.instance().updateData('commessa', res[0])
            popupNuovaCommessaRef.current?.instance().hide()
          })
        }}
      ></FormPopupProject>
      <FormPopupClientNote
        data={activity.sede}
        refPopup={popupNoteClienteRef}
        refreshDataSourceSites={() => {
          if (currentCompany) {
            getCompanySites().then((sites: sede[]) => {
              console.log('SITES', sites)
              setSediDataSource(ArraySourceComposer('id', sites))
              formRef.current?.instance().updateData(
                'sede',
                sites.find((site: sede) => {
                  return site.id === activity.sede?.id
                }),
              )
            })
          }
        }}
        isRoleAllowed={isUserRoleAllowed(userInfo?.roles, [
          Roles.TechDeveloper,
          Roles.Sales,
          Roles.GlobalAdministrator,
          Roles.ExternalSales,
          Roles.Administrator,
        ])}
      ></FormPopupClientNote>
    </>
  )
}

export default ActivityEditorForm
